// src/features/affiliate/badges.tsx
// Status → Badge variant mapping for every affiliate status enum.

import { Badge } from '@react/components';
import { useI18n } from '@react/hooks';
import { enumKey } from './format';

type Variant = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error';

function variantFor(map: Record<string, Variant>, key: string): Variant {
  return map[key] ?? 'default';
}

const AFFILIATE_VARIANTS: Record<string, Variant> = {
  pending: 'warning',
  active: 'success',
  suspended: 'error',
  rejected: 'error',
  banned: 'error',
};

const OFFER_VARIANTS: Record<string, Variant> = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  archived: 'default',
};

const CAMPAIGN_VARIANTS: Record<string, Variant> = {
  active: 'success',
  paused: 'warning',
  archived: 'default',
};

const CONVERSION_VARIANTS: Record<string, Variant> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  hold: 'secondary',
};

const PAYOUT_VARIANTS: Record<string, Variant> = {
  pending: 'warning',
  approved: 'secondary',
  processing: 'secondary',
  completed: 'success',
  rejected: 'error',
  failed: 'error',
};

const TIER_VARIANTS: Record<string, Variant> = {
  active: 'success',
  inactive: 'default',
};

export function AffiliateStatusBadge({ status }: { status?: string }) {
  const { t } = useI18n();
  const key = enumKey(status, 'AFFILIATE_STATUS_');
  return <Badge variant={variantFor(AFFILIATE_VARIANTS, key)}>{t(`affiliate.status.${key}`)}</Badge>;
}

export function OfferStatusBadge({ status }: { status?: string }) {
  const { t } = useI18n();
  const key = enumKey(status, 'OFFER_STATUS_');
  return <Badge variant={variantFor(OFFER_VARIANTS, key)}>{t(`affiliate.offerStatus.${key}`)}</Badge>;
}

export function CampaignStatusBadge({ status }: { status?: string }) {
  const { t } = useI18n();
  const key = enumKey(status, 'CAMPAIGN_STATUS_');
  return <Badge variant={variantFor(CAMPAIGN_VARIANTS, key)}>{t(`affiliate.campaignStatus.${key}`)}</Badge>;
}

export function ConversionStatusBadge({ status }: { status?: string }) {
  const { t } = useI18n();
  const key = enumKey(status, 'CONVERSION_STATUS_');
  return <Badge variant={variantFor(CONVERSION_VARIANTS, key)}>{t(`affiliate.conversionStatus.${key}`)}</Badge>;
}

export function PayoutStatusBadge({ status }: { status?: string }) {
  const { t } = useI18n();
  const key = enumKey(status, 'PAYOUT_STATUS_');
  return <Badge variant={variantFor(PAYOUT_VARIANTS, key)}>{t(`affiliate.payoutStatus.${key}`)}</Badge>;
}

export function TierStatusBadge({ status }: { status?: string }) {
  const { t } = useI18n();
  const key = enumKey(status, 'COMMISSION_TIER_STATUS_');
  return <Badge variant={variantFor(TIER_VARIANTS, key)}>{t(`affiliate.tierStatus.${key}`)}</Badge>;
}

export function offerPayoutTypeLabel(t: (k: string) => string, value?: string): string {
  return t(`affiliate.payoutType.${enumKey(value, 'OFFER_PAYOUT_TYPE_')}`);
}
